import { useRef, type ChangeEvent } from "react";
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Icon } from "@common/index";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import type { ProductImage } from "@models/product";
import { SortableImageTile } from "./_components/SortableImageTile";

interface ProductImageManagerProps {
    productId: string;
    images: ProductImage[];
    /** Re-fetch the product so the grid reflects persisted state after a mutation. */
    onChanged: () => void;
    /** Disable controls while a save is in flight. */
    disabled?: boolean;
}

// The primary image is the one flagged isPrimary; fall back to the first image
// so the UI still marks one when the flag is somehow absent.
const resolvePrimaryId = (images: ProductImage[]): string =>
    images.find((image) => image.isPrimary)?.id ?? images[0]?.id ?? "";

// Interactive image-management section for the Edit Product page (E3-S4 #170):
// upload, remove, drag-reorder, and set-primary. Reorder and set-primary both
// persist through the order endpoint, which requires the FULL ordered id set.
export const ProductImageManager = ({
    productId,
    images,
    onChanged,
    disabled = false,
}: ProductImageManagerProps) => {
    const { addImages, removeImage, orderImages } = useProductApi();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const imageIds = images.map((image) => image.id);
    const primaryId = resolvePrimaryId(images);
    const primaryImage =
        images.find((image) => image.id === primaryId) ?? images[0];

    const persistOrder = async (orderedIds: string[], newPrimaryId: string) => {
        await orderImages(productId, orderedIds, newPrimaryId);
        onChanged();
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = imageIds.indexOf(String(active.id));
        const newIndex = imageIds.indexOf(String(over.id));

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        // Send the FULL reordered id set; the current primary is unchanged.
        await persistOrder(arrayMove(imageIds, oldIndex, newIndex), primaryId);
    };

    const handleSetPrimary = async (newPrimaryId: string) => {
        if (newPrimaryId === primaryId) {
            return;
        }

        // Keep the current order; only the primary changes (set exclusively).
        await persistOrder(imageIds, newPrimaryId);
    };

    const handleRemove = async (imageId: string) => {
        await removeImage(productId, imageId);
        onChanged();
    };

    const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        // The gate is a correctness one (the image endpoints 404 on an
        // inactive product), and the OS file dialog outlives a re-render: a
        // pick made after the manager was disabled must not POST.
        if (disabled || !files || files.length === 0) {
            return;
        }

        try {
            await addImages(productId, Array.from(files));
            onChanged();
        } finally {
            // Allow re-selecting the same file later, even if the upload failed.
            event.target.value = "";
        }
    };

    return (
        <div>
            <div className="aspect-[3/2] w-full rounded bg-base-300 overflow-hidden flex items-center justify-center">
                {primaryImage ? (
                    <img
                        src={primaryImage.url}
                        alt="Primary product"
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <span className="text-sm text-gray-500">No image</span>
                )}
            </div>

            <div className="mt-3 flex gap-3 flex-wrap items-center">
                <DndContext
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={imageIds}
                        strategy={horizontalListSortingStrategy}
                    >
                        {images.map((image) => (
                            <SortableImageTile
                                key={image.id}
                                image={image}
                                isPrimary={image.id === primaryId}
                                onSetPrimary={handleSetPrimary}
                                onRemove={handleRemove}
                                disabled={disabled}
                            />
                        ))}
                    </SortableContext>
                </DndContext>

                <button
                    type="button"
                    aria-label="Add images"
                    disabled={disabled}
                    className="h-16 w-16 shrink-0 rounded border-2 border-dashed border-base-300 flex items-center justify-center text-gray-500"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Icon name="add" className="text-2xl" />
                </button>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    aria-label="Upload images"
                    disabled={disabled}
                    onChange={handleUpload}
                />
            </div>
        </div>
    );
};
